import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/useAuth';
import RoleGate from '../../auth/RoleGate';
import StatusBadge from './StatusBadge';
import { Eye, Clock, Edit, History, Download, AlertTriangle } from 'lucide-react';

export default function EntryHeader({ entry }) {
    const { lang, hasRole } = useAuth();
    const navigate = useNavigate();

    const title = lang === 'en' && entry.title_en
        ? entry.title_en
        : (lang === 'vi' && entry.title_vi) || entry.title_en || entry.title || 'Untitled';

    const isEditor = hasRole('editor');

    return (
        <div style={{ marginBottom: '1.5rem' }}>
            {/* Title row */}
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
                <div style={{ flex: 1 }}>
                    <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text-primary)', margin: 0, lineHeight: 1.3 }}>
                        {title}
                    </h1>
                    {/* Lang fallback chip */}
                    {lang === 'en' && !entry.title_en && entry.title_vi && (
                        <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', background: 'var(--bg-tertiary)', padding: '0.1rem 0.4rem', borderRadius: '3px' }}>
                            [VI only]
                        </span>
                    )}
                    {lang === 'vi' && !entry.title_vi && entry.title_en && (
                        <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', background: 'var(--bg-tertiary)', padding: '0.1rem 0.4rem', borderRadius: '3px' }}>
                            [EN only]
                        </span>
                    )}
                </div>

                {/* Meta badges */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexShrink: 0 }}>
                    {entry.status && <StatusBadge status={entry.status} />}

                    {/* View count */}
                    <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
                        <Eye size={12} /> {entry.view_count || 0}
                    </span>

                    {/* Last edited */}
                    {entry.updated_at && (
                        <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
                            <Clock size={12} />
                            {new Date(entry.updated_at).toLocaleDateString()}
                        </span>
                    )}
                </div>
            </div>

            {/* Action buttons — editor+ */}
            <RoleGate min="editor">
                <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
                    <button
                        className="btn btn-primary"
                        style={{ padding: '0.4rem 1rem', fontSize: '0.8rem' }}
                        onClick={() => navigate(`/wiki/entry/${entry.slug || entry.id}/edit`)}
                    >
                        <Edit size={14} /> Edit
                    </button>
                    <button
                        className="btn btn-secondary"
                        style={{ padding: '0.4rem 1rem', fontSize: '0.8rem' }}
                        onClick={() => navigate(`/wiki/entry/${entry.slug || entry.id}/history`)}
                    >
                        <History size={14} /> History
                    </button>
                    <button
                        className="btn btn-secondary"
                        style={{ padding: '0.4rem 1rem', fontSize: '0.8rem' }}
                        onClick={() => window.open(`/api/wiki/entries/${entry.id}/export?format=pdf`, '_blank')}
                    >
                        <Download size={14} /> Export PDF
                    </button>
                </div>
            </RoleGate>

            {/* Subtitle / category */}
            {entry.category && (
                <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                    Category: <strong style={{ color: 'var(--gold)' }}>{entry.category.name || entry.category}</strong>
                    {entry.vendor_name && <> — Vendor: <strong>{entry.vendor_name}</strong></>}
                </div>
            )}
        </div>
    );
}